import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { HStack } from '@/components/gluestack/hstack';
import { Text, ITextProps } from '@/components/gluestack/text';
import { LikeButton } from '@/components/domain/common/LikeButton';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { Comment01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { CommentsSheet } from '@/components/domain/comments/CommentsSheet';
import { MemberListSheet } from '@/components/domain/common/MemberListSheet';
import { useFormatCount } from '@/hooks/useFormatCount';
import { Photo } from '@/models/Photo';

interface ILikesCommentsOverlayProps {
  photo?: Photo | null;
  iconSize?: number;
  commentIconSize?: number;
  textSize?: ITextProps['size'];
  iconClassName?: string;
  className?: string;
  onOpenComments?: () => void;
  onOpenLikes?: () => void;
  disabled?: boolean;
  onBottomSheetOpenChange?: (open: boolean) => void;
}

export const LikesCommentsOverlayComponent: React.FC<ILikesCommentsOverlayProps> = ({
  photo,
  iconSize = 24,
  commentIconSize = 22,
  textSize = 'md',
  iconClassName = 'text-white',
  className = '',
  onOpenComments,
  onOpenLikes,
  disabled = false,
  onBottomSheetOpenChange,
}) => {
  const { t } = useTranslation();
  const { formatCount } = useFormatCount();
  const [isCommentsSheetOpen, setIsCommentsSheetOpen] = useState(false);
  const [isLikesSheetOpen, setIsLikesSheetOpen] = useState(false);

  if (!photo) return null;

  return (
    <View>
      <HStack className={`items-center rounded-full px-4 py-2 ${className}`}>
        <LikeButton item={photo} itemType="photo" size={iconSize} iconClassName={iconClassName} disabled={disabled} />
        {photo.LikesCount > 0 && (
          <Pressable 
            onPress={() => !disabled && (onOpenLikes ? onOpenLikes() : (setIsLikesSheetOpen(true), onBottomSheetOpenChange?.(true)))} 
            hitSlop={{ top: 4, bottom: 4, left: 4, right: 16 }}
            disabled={disabled}
          >
            <Text size={textSize} className="ml-2 font-semibold text-white">
              {formatCount(photo.LikesCount)}
            </Text>
          </Pressable>
        )}
        <View className="mx-4 h-5 w-px bg-white/50" />
        <Pressable 
          onPress={() => !disabled && (onOpenComments ? onOpenComments() : (setIsCommentsSheetOpen(true), onBottomSheetOpenChange?.(true)))} 
          className="flex-row items-center" 
          hitSlop={8}
          disabled={disabled}
        >
          <HugeIconsWrapper icon={Comment01Icon} size={commentIconSize} className={iconClassName} />
          {photo.CommentsCount > 0 && (
            <Text size={textSize} className="ml-2 font-semibold text-white">
              {formatCount(photo.CommentsCount)}
            </Text>
          )}
        </Pressable>
      </HStack>

      {/* Uncontrolled mode: render internal sheets only when callbacks are not provided */}
      {!onOpenComments && (
        <ContextSheet
          isOpen={isCommentsSheetOpen}
          onClose={() => { setIsCommentsSheetOpen(false); onBottomSheetOpenChange?.(false); }}
          onWillClose={() => onBottomSheetOpenChange?.(false)}
          contentComponent={CommentsSheet}
          title={t('comments.title')}
          expectsKeyboard={photo.CommentsCount === 0}
          payload={{ photo }}
        />
      )}
      {!onOpenLikes && (
        <ContextSheet
          isOpen={isLikesSheetOpen}
          onClose={() => { setIsLikesSheetOpen(false); onBottomSheetOpenChange?.(false); }}
          onWillClose={() => onBottomSheetOpenChange?.(false)}
          contentComponent={MemberListSheet}
          title={t('likes.title')}
          payload={{ listType: 'photo-likes', photo }}
        />
      )}
    </View>
  );
};

export default LikesCommentsOverlayComponent;


